import { navigate } from 'gatsby';
import { useCallback, useMemo, useState } from 'react';

import { paths } from '@/constants/paths';

export enum ProcessStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  SUCCESS = 'success',
  FAILED = 'failed',
}

type StatusProcess = ProcessStatus;

type UseServiceMethodReturn<T extends unknown[], R> = {
  status: StatusProcess;
  error: string | null;
  response: R | null;
  execute: (...params: T) => Promise<R>;
};

export function useServiceMethod<T extends unknown[], R>(
  serviceMethod: (...args: T) => Promise<R>,
): UseServiceMethodReturn<T, R> {
  const [status, setStatus] = useState<StatusProcess>(ProcessStatus.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<R | null>(null);

  const execute = useCallback(
    async (...params: T) => {
      setStatus(ProcessStatus.RUNNING);
      setError(null);
      setResponse(null);
      try {
        const data = await serviceMethod(...params);
        setStatus(ProcessStatus.SUCCESS);
        setResponse(data);
        return data;
      } catch (err) {
        setStatus(ProcessStatus.FAILED);
        if (err instanceof Error) {
          setError(err.message);
        }
        //@ts-expect-error atributo de referência de erro 5XX da API
        if (err?.isInternalServerError) {
          navigate(paths.internalServerError);
          return;
        }
        throw err;
      }
    },
    [serviceMethod],
  );

  return useMemo(
    () => ({
      status,
      error,
      response,
      execute,
    }),
    [status, error, response, execute],
  );
}
